import React, { useRef, useState, useImperativeHandle, useMemo, useCallback } from "react";
import BootstrapModalConfirm from "components/bootstrap/BootstrapModalConfirm";
import { PaginationProvider, SelectedRowsProvider, useSelectedRows } from "common/contexts";
import InvestigationForm from "security-app/components/Investigations/InvestigationForm";
import { useGetSecurityEvent } from "security-app/hooks/useSecurityEventsAPI";
import { useGetUsers, useGetPriorities } from "security-app/hooks/useInvestigationsAPI";
import EventDefinitionPriorityEnum from "logic/alerts/EventDefinitionPriorityEnum";
import useEventActionTelemetry from "components/events/events/hooks/useSendEventActionTelemetry";
import UpdateEventStatusModal from "security-app/components/Investigations/plugin/shared/UpdateEventStatusModal";
import useSecurityEventEditCondition from "security-app/components/Investigations/plugin/useSecurityEventEditCondition";
import SearchRow from "./Search";
import List from "./List";
import useStatusMap from "./useStatusMap";
const modalTitleByType = {
  logs: "Add log",
  dashboards: "Add dashboard",
  searches: "Add search",
  events: "Add event"
};
const ConfirmModal = ({ ...props }) => {
  const selectedInvestigations = useSelectedRows();
  return /* @__PURE__ */ React.createElement(BootstrapModalConfirm, { ...props, confirmButtonDisabled: selectedInvestigations.length === 0 });
};
const AddEvidenceModal = ({
  type,
  ids,
  index = void 0,
  searchMeta = void 0,
  show = false,
  onClose = void 0,
  onCompleted = void 0,
  fromBulk = false,
  onSubmit = void 0,
  children = void 0
}, ref) => {
  const listRef = useRef(null);
  const [showModal, setShowModal] = useState(show);
  const [showNewInvestigationModal, setShowNewInvestigationModal] = useState(false);
  const sendEventActionTelemetry = useEventActionTelemetry();
  const canEditSecurityEvents = useSecurityEventEditCondition();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [selectedInvestigationIds, setSelectedInvestigationIds] = useState([]);
  const [createdInvestigation, setCreatedInvestigation] = useState(void 0);
  const { securityEvent } = useGetSecurityEvent(type === "events" && ids.length === 1 ? ids[0] : void 0);
  const statusMap = useStatusMap();
  const { users } = useGetUsers(true);
  const canBeAssigned = useCallback(
    (userId) => users.find((user) => user.id === userId) !== void 0,
    [users]
  );
  const { priorities } = useGetPriorities(true);
  const getInvestigationPriority = useCallback(
    (eventPriority) => {
      var _a;
      const eventPriorityName = (_a = EventDefinitionPriorityEnum.properties[eventPriority]) == null ? void 0 : _a.name;
      return priorities.find(
        (priority) => priority.text.toLowerCase() === (eventPriorityName == null ? void 0 : eventPriorityName.toLowerCase())
      ) || null;
    },
    [priorities]
  );
  const initInvestigation = useMemo(() => {
    var _a;
    if (type === "events" && securityEvent) {
      return {
        name: `Investigation for ${securityEvent.event.message}`,
        assigned_to: canBeAssigned(securityEvent.owner) ? securityEvent.owner : null,
        priority: ((_a = getInvestigationPriority(securityEvent.event.priority)) == null ? void 0 : _a.priority) || null,
        status: statusMap[securityEvent.status] || null,
        notes: securityEvent.notes || null
      };
    }
    return void 0;
  }, [canBeAssigned, getInvestigationPriority, securityEvent, statusMap, type]);
  const closeModal = () => {
    var _a, _b;
    (_b = (_a = listRef.current) == null ? void 0 : _a.onCancel) == null ? void 0 : _b.call(_a);
    setShowModal(false);
    setCreatedInvestigation(void 0);
    if (onClose) onClose();
  };
  const onConfirm = async () => {
    var _a, _b;
    const currentSelectedInvestigations = ((_b = (_a = listRef.current) == null ? void 0 : _a.getSelectedInvestigations) == null ? void 0 : _b.call(_a)) || [];
    await listRef.current.onConfirm();
    setCreatedInvestigation(void 0);
    if (onCompleted) onCompleted();
    if (type === "events" && currentSelectedInvestigations.length > 0 && canEditSecurityEvents) {
      sendEventActionTelemetry("SELECT_AN_INVESTIGATION", fromBulk);
      setSelectedInvestigationIds(currentSelectedInvestigations.map((investigation) => investigation.id));
      setShowUpdateStatusModal(true);
    } else {
      if (type === "events") {
        sendEventActionTelemetry("SELECT_AN_INVESTIGATION", fromBulk);
      }
      if (onClose) onClose();
    }
  };
  useImperativeHandle(ref, () => ({
    toggle: () => setShowModal(true)
  }));
  const onCloseNewInvestigationModal = () => {
    setShowNewInvestigationModal(false);
    setShowModal(true);
  };
  const onNewInvestigation = () => {
    setShowModal(false);
    setShowNewInvestigationModal(true);
  };
  return /* @__PURE__ */ React.createElement(SelectedRowsProvider, null, /* @__PURE__ */ React.createElement(
    ConfirmModal,
    {
      size: "lg",
      showModal,
      onCancel: closeModal,
      onConfirm,
      isAsyncSubmit: true,
      isSubmitting,
      submitLoadingText: "Adding to investigation...",
      title: `Investigations - ${modalTitleByType[type]} as evidence`
    },
    children,
    /* @__PURE__ */ React.createElement(PaginationProvider, null, /* @__PURE__ */ React.createElement(SearchRow, { onNewInvestigation }), /* @__PURE__ */ React.createElement(
      List,
      {
        type,
        ids,
        index,
        createdInvestigation,
        setShowModal,
        setIsSubmitting,
        onSubmit,
        searchMeta,
        ref: listRef
      }
    ))
  ), showNewInvestigationModal && /* @__PURE__ */ React.createElement(
    InvestigationForm,
    {
      initialValues: initInvestigation,
      onCreate: setCreatedInvestigation,
      onClose: onCloseNewInvestigationModal
    }
  ), type === "events" && selectedInvestigationIds.length > 0 && /* @__PURE__ */ React.createElement(
    UpdateEventStatusModal,
    {
      investigationIds: selectedInvestigationIds,
      show: showUpdateStatusModal,
      onClose: () => {
        setShowUpdateStatusModal(false);
        setSelectedInvestigationIds([]);
        if (onClose) onClose();
      }
    }
  ));
};
export default React.forwardRef(AddEvidenceModal);
